import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogProvider";
import { useSite } from "@/context/SiteContext";
import {
  FolderArchive, Bug, Building2, FileText, Plus,
  Upload, Trash2, Download, CheckCircle2, XCircle, Clock, AlertTriangle, ChevronRight, Star,
  FolderOpen,
} from "lucide-react";
import ComplianceDocsSection from "@/pages/ComplianceDocsSection";

/**
 * Documentation
 *
 * Landing tile dashboard + sub-pages for each tracked requirement.
 * Two requirements ship in v1 — Pest Control (per-site) and Approved
 * Vendor (tenant-wide). Future requirements (calibration certs, FSMS
 * policy docs, …) plug in as additional `TILES` entries + their own
 * sub-component.
 *
 * Site-filter behaviour:
 *   - Pest Control is per-site → master site filter gates the detail view.
 *   - Approved Vendor list is tenant-wide → ignores the site filter (with
 *     a banner noting this).
 */
const REQUIREMENTS = [
  { key: "pest_control",     label: "Pest Control",     icon: Bug,         component: PestControlSection,     feature: "pest-control" },
  { key: "approved_vendors", label: "Approved Vendor",  icon: Building2,   component: ApprovedVendorSection,  feature: "approved-vendor" },
  { key: "compliance_docs",  label: "Compliance Documents", icon: FolderOpen, component: ComplianceDocsSection, feature: "compliance-documents" },
];

export default function Documentation() {
  const { notify } = useDialog();
  const { isFeatureEnabled } = useAuth();
  const { activeSiteId } = useSite();
  const [view, setView] = useState("home");    // "home" | <requirement key>
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/documentation/summary");
      setSummary(data);
    } catch (e) {
      await notify({ title: "Couldn't load documentation summary",
        message: formatErr(e.response?.data?.detail) || "Try again", variant: "err" });
    } finally { setBusy(false); }
  };
  // Re-fetch when the master site filter changes so the Pest Control
  // tile reflects only the sites the user has currently scoped to.
  useEffect(() => { load(); }, [activeSiteId]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (view !== "home") {
    const req = REQUIREMENTS.find((r) => r.key === view);
    // Guard: if the tenant's feature for the open section was turned off
    // (e.g. Super-Admin toggled it while the tab was open), bounce back
    // to home so the disabled sub-page isn't rendered.
    if (req && !isFeatureEnabled(req.feature)) {
      setView("home");
      return null;
    }
    const Comp = req?.component;
    return (
      <div className="space-y-4" data-testid={`docs-section-${view}`}>
        <button className="vb-btn ghost sm" onClick={() => { setView("home"); load(); }}
          data-testid="docs-back">
          ← Back to Documentation
        </button>
        {Comp && <Comp />}
      </div>
    );
  }

  const showPest     = isFeatureEnabled("pest-control");
  const showVendor   = isFeatureEnabled("approved-vendor");
  const showCompDocs = isFeatureEnabled("compliance-documents");

  return (
    <div className="space-y-5" data-testid="docs-home">
      <div>
        <h1 className="vb-heading text-3xl m-0 flex items-center gap-2" style={{ color: "var(--vb-text-2)" }}>
          <FolderArchive size={24} /> Documentation
        </h1>
        <p className="text-sm opacity-70 m-0">
          Track required ISO 22000 documentation — vendor declarations, pest control records, and supporting files.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {showPest     && <PestControlTile     summary={summary?.pest_control}     busy={busy} onOpen={() => setView("pest_control")} />}
        {showVendor   && <ApprovedVendorTile  summary={summary?.approved_vendors} busy={busy} onOpen={() => setView("approved_vendors")} />}
        {showCompDocs && <ComplianceDocsTile  summary={summary?.compliance_docs}  busy={busy} onOpen={() => setView("compliance_docs")} />}
      </div>

      {!showPest && !showVendor && !showCompDocs && (
        <div className="vb-card p-6 text-sm opacity-70 text-center" data-testid="docs-none-enabled">
          No documentation modules are enabled for this workspace. Contact your platform administrator.
        </div>
      )}

      <div className="vb-card p-4 text-[12px] opacity-70" data-testid="docs-hint">
        <strong>Tip:</strong> Each requirement tile shows progress at a glance. Click a tile to upload or review the underlying documents. The master site filter applies to per-site requirements (e.g. Pest Control); tenant-wide lists like the Approved Vendor master ignore it.
      </div>
    </div>
  );
}

/* ------------------------- TILES ------------------------- */

function Tile({ icon: Icon, title, pct, count, countLabel = "files", body, footer, onOpen, testid, color = "var(--vb-accent)" }) {
  const showCount = count !== undefined;
  return (
    <button onClick={onOpen} className="vb-card p-5 text-left hover:shadow-md transition-shadow"
      data-testid={testid} style={{ display: "block", cursor: "pointer", width: "100%" }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon size={20} style={{ color }} />
          <h3 className="vb-heading text-lg m-0" style={{ color: "var(--vb-text-2)" }}>{title}</h3>
        </div>
        <ChevronRight size={18} className="opacity-50" />
      </div>
      {showCount ? (
        <div className="flex items-baseline gap-2 mb-3">
          <span className="vb-heading text-3xl m-0" style={{ color }} data-testid={`${testid}-count`}>{count}</span>
          <span className="text-xs opacity-60">{countLabel}</span>
        </div>
      ) : (
        <>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="vb-heading text-3xl m-0" style={{ color }}>{pct}%</span>
            <span className="text-xs opacity-60">complete</span>
          </div>
          <div className="h-1.5 rounded mb-3" style={{ background: "var(--vb-border)" }}>
            <div className="h-full rounded" style={{ width: `${Math.min(100, pct)}%`, background: color }} />
          </div>
        </>
      )}
      <div className="text-[12px] opacity-80">{body}</div>
      {footer && <div className="text-[11px] opacity-60 mt-2">{footer}</div>}
    </button>
  );
}

function PestControlTile({ summary, busy, onOpen }) {
  const pct = summary?.pct ?? 0;
  const complete = summary?.sites_complete ?? 0;
  const total = summary?.sites_total ?? 0;
  const month = summary?.current_month || "—";
  return (
    <Tile icon={Bug} title="Pest Control" pct={busy ? 0 : pct} testid="tile-pest-control"
      color={pct >= 80 ? "var(--vb-accent)" : pct >= 40 ? "var(--vb-warning)" : "var(--vb-error)"}
      body={total === 0
        ? "No sites yet. Add a site to start tracking pest control."
        : `${complete} of ${total} site${total > 1 ? "s" : ""} fully compliant for ${month}.`}
      footer="Per-site: vendor, license, site map, trap layout, MSDS, monthly visit."
      onOpen={onOpen} />
  );
}

function ApprovedVendorTile({ summary, busy, onOpen }) {
  const pct = summary?.pct ?? 0;
  const total = summary?.total ?? 0;
  const active = summary?.active_approved ?? 0;
  const dueSoon = summary?.due_soon ?? 0;
  const overdue = summary?.overdue ?? 0;
  return (
    <Tile icon={Building2} title="Approved Vendor" pct={busy ? 0 : pct} testid="tile-approved-vendors"
      color={overdue > 0 ? "var(--vb-error)" : dueSoon > 0 ? "var(--vb-warning)" : "var(--vb-accent)"}
      body={total === 0
        ? "No vendors recorded yet. Add your first vendor to start tracking."
        : `${active} of ${total} vendor${total > 1 ? "s" : ""} fully approved and in-window.`}
      footer={(dueSoon > 0 || overdue > 0)
        ? `${dueSoon} due for re-eval · ${overdue} overdue`
        : "All vendors within annual re-evaluation window."}
      onOpen={onOpen} />
  );
}

function ComplianceDocsTile({ summary, busy, onOpen }) {
  const active = summary?.active ?? 0;
  const total = summary?.total ?? 0;
  const folders = summary?.folders ?? 0;
  return (
    <Tile icon={FolderOpen} title="Compliance Documents"
      count={busy ? 0 : active}
      countLabel={active === 1 ? "active file" : "active files"}
      testid="tile-compliance-docs" color="var(--vb-accent)"
      body={total === 0 && folders === 0
        ? "Set up a folder tree (Manual, Procedures, Forms) and start uploading your controlled documents."
        : total === 0
        ? `${folders} folder${folders !== 1 ? "s" : ""} ready — upload your first document.`
        : `${active} active · ${total - active} obsolete · ${folders} folder${folders !== 1 ? "s" : ""}.`}
      footer="Central filing cabinet with document-control master list export."
      onOpen={onOpen} />
  );
}

/* ------------------------- PEST CONTROL SUB-VIEW ------------------------- */

function PestControlSection() {
  const { notify, confirm } = useDialog();
  const { user } = useAuth();
  const { activeSiteId, isAllSites, sites } = useSite();
  const [data, setData] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [busy, setBusy] = useState(false);

  // Per-site Pest Annual filing was retired from this page (2026-06-23) —
  // all Documentation filing now lives on the Reports page so users have
  // one consistent place. Local handler removed; deep-link from the
  // Audit Vault still works because the vault keeps the report type.

  const load = async () => {
    setBusy(true);
    try {
      const [pest, vens] = await Promise.all([
        api.get("/pest-control"),
        api.get("/vendors", { params: { category: "Pest Control" } }),
      ]);
      setData(pest.data);
      setVendors(vens.data.vendors || []);
    } catch (e) {
      await notify({ title: "Couldn't load Pest Control", message: formatErr(e.response?.data?.detail), variant: "err" });
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [activeSiteId]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (busy && !data) return <div className="vb-card p-6 opacity-70" data-testid="pest-loading">Loading…</div>;
  if (!data) return null;

  const rows = data.sites || [];
  const showDetail = !isAllSites;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h2 className="vb-heading text-2xl m-0 flex items-center gap-2" style={{ color: "var(--vb-text-2)" }}>
            <Bug size={20} /> Pest Control
          </h2>
          <p className="text-sm opacity-70 m-0">
            Tracks per-site Pest Bait Layout and monthly visit reports. Vendor-level docs (Evaluation form, Contract, License, SDS) are managed on the Approved Vendor page.
          </p>
        </div>
      </div>

      {!showDetail && (
        <div className="vb-card p-3 text-[12px]" data-testid="pest-allsites-banner"
          style={{ background: "var(--vb-bg-2,#faf6f1)" }}>
          <strong>All Sites selected.</strong> Showing high-level completion per site below. Switch the master site filter (top bar) to a specific site to upload documents or log this month&apos;s visit.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {rows.map((s) => (
          <PestSiteCard key={s.site_id} site={s} vendors={vendors}
            showActions={showDetail} onChanged={load} />
        ))}
        {rows.length === 0 && (
          <div className="vb-card p-6 opacity-70" data-testid="pest-empty">
            No sites visible. Ask an owner to add sites first.
          </div>
        )}
      </div>
    </div>
  );
}

function PestSiteCard({ site, vendors, showActions, onChanged }) {
  const { user } = useAuth();
  const { notify, confirm } = useDialog();
  const [showAssign, setShowAssign] = useState(false);
  const [picked, setPicked] = useState("");
  const canEdit = ["owner", "manager"].includes(user?.role);
  const canUpload = ["owner", "manager", "supervisor"].includes(user?.role);

  const pct = Math.round(100 * site.completion.done / site.completion.total);
  const color = pct === 100 ? "var(--vb-accent)" : pct >= 50 ? "var(--vb-warning)" : "var(--vb-error)";

  // Look up the assigned vendor's approval state so we can surface a
  // "Pending Approval" banner directly on the site card.
  const assignedVendor = site.vendor_id ? vendors.find((v) => v.id === site.vendor_id) : null;
  const isPendingApproval = !!assignedVendor && assignedVendor.evaluation_outcome !== "approve" && !assignedVendor.performance_delisted;

  const assign = async () => {
    if (!picked) return;
    try {
      const fd = new FormData();
      fd.append("site_id", site.site_id);
      fd.append("vendor_id", picked);
      await api.post("/pest-control/assign", fd);
      setShowAssign(false); setPicked("");
      onChanged();
    } catch (e) {
      await notify({ title: "Couldn't assign vendor", message: formatErr(e.response?.data?.detail), variant: "err" });
    }
  };

  const unassign = async () => {
    const ok = await confirm({ title: "Remove vendor assignment?", message: `${site.vendor_name} will no longer be linked to ${site.site_name} for pest control.` });
    if (!ok) return;
    try {
      await api.delete("/pest-control/assign", { params: { site_id: site.site_id } });
      onChanged();
    } catch (e) {
      await notify({ title: "Couldn't remove", message: formatErr(e.response?.data?.detail), variant: "err" });
    }
  };

  return (
    <div className="vb-card p-4" data-testid={`pest-site-${site.site_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="vb-heading text-lg m-0 flex items-center gap-2" style={{ color: "var(--vb-text-2)" }}>
            <Building2 size={16} /> {site.site_name}
          </h3>
          {site.vendor_name ? (
            <div className="text-[12px] opacity-80">Vendor: <strong>{site.vendor_name}</strong></div>
          ) : (
            <div className="text-[12px]" style={{ color: "var(--vb-error)" }}>No vendor assigned</div>
          )}
        </div>
        <div className="text-right">
          <div className="vb-heading text-xl m-0" style={{ color }}>{site.completion.done}/{site.completion.total}</div>
          <div className="text-[10px] opacity-60">complete</div>
        </div>
      </div>

      <div className="h-1.5 rounded mb-3" style={{ background: "var(--vb-border)" }}>
        <div className="h-full rounded" style={{ width: `${pct}%`, background: color }} />
      </div>

      {isPendingApproval && (
        <div className="flex items-center gap-2 px-3 py-2 rounded mb-3 text-[12px]"
          data-testid={`pest-pending-banner-${site.site_id}`}
          style={{
            background: "rgba(245,158,11,0.10)",
            border: "1px solid rgba(245,158,11,0.45)",
            color: "var(--vb-text-2)",
          }}>
          <AlertTriangle size={14} style={{ color: "var(--vb-warning)" }} />
          <span>
            <strong>{site.vendor_name}</strong> is undergoing approval — review the vendor record on the
            Approved Vendor page to complete sign-off.
          </span>
        </div>
      )}

      <ul className="text-[12px] m-0 p-0" style={{ listStyle: "none" }}>
        {site.completed.map((c) => (
          <li key={c} className="flex items-center gap-2" style={{ color: "var(--vb-accent)" }}>
            <CheckCircle2 size={12} /> {c}
          </li>
        ))}
        {site.missing.map((m) => (
          <li key={m} className="flex items-center gap-2 opacity-80">
            <XCircle size={12} style={{ color: "var(--vb-error)" }} /> {m}
          </li>
        ))}
      </ul>

      {showActions && canEdit && (
        <div className="flex gap-2 mt-3 flex-wrap">
          {!site.vendor_id ? (
            <button className="vb-btn sm" onClick={() => setShowAssign(true)}
              data-testid={`pest-assign-${site.site_id}`}>
              <Plus size={12} /> Assign vendor
            </button>
          ) : (
            <button className="vb-btn sm ghost" onClick={unassign}
              data-testid={`pest-unassign-${site.site_id}`}>
              <Trash2 size={12} /> Remove vendor
            </button>
          )}
        </div>
      )}

      {showActions && site.vendor_id && (
        <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--vb-border)" }}>
          {/* Pest Bait Layout — one-time per-site upload. */}
          <DocumentSlot label="Pest Bait Layout" ownerType="pest_site" ownerId={site.site_id}
            siteId={site.site_id} requirementKind="pest_bait_layout"
            canUpload={canUpload} onChanged={onChanged} expiryHint="Per-site bait station layout" />
          {/* 12-month visit-report tracker (current year always shown,
              past years collapsed into a single summary row). */}
          <MonthlyVisitTracker site={site} canUpload={canUpload} onChanged={onChanged} />
          <div className="text-[11px] opacity-60 mt-2">
            Vendor-level documents (Evaluation form, Contract, License, SDS) live on the
            <strong> {site.vendor_name}</strong> record in the Approved Vendor page.
          </div>
        </div>
      )}

      {showAssign && (
        <AssignVendorModal vendors={vendors} siteId={site.site_id} picked={picked} setPicked={setPicked}
          onConfirm={assign} onClose={() => { setShowAssign(false); setPicked(""); }} />
      )}
    </div>
  );
}

function AssignVendorModal({ vendors, siteId, picked, setPicked, onConfirm, onClose }) {
  // A vendor is selectable only when it serves this site OR all sites.
  // Delisted vendors (performance score < 15) are excluded — they're not
  // eligible to take on new assignments until re-evaluated.
  const eligible = vendors.filter((v) =>
    !v.performance_delisted
    && (v.site_assignments === "all" || (Array.isArray(v.site_assignments) && v.site_assignments.includes(siteId))),
  );
  return (
    <Modal title="Assign pest control vendor" onClose={onClose} testid="pest-assign-modal">
      {eligible.length === 0 ? (
        <div className="text-[13px] opacity-80">
          No eligible vendors in the <strong>Pest Control</strong> category for this site yet.
          Add one in the Approved Vendor section first. <em>Delisted vendors are hidden until re-evaluated.</em>
        </div>
      ) : (
        <>
          <label className="block text-[12px] font-medium mb-1">Vendor</label>
          <select className="vb-input" value={picked} onChange={(e) => setPicked(e.target.value)}
            data-testid="pest-assign-select">
            <option value="">— pick a vendor —</option>
            {eligible.map((v) => (
              <option key={v.id} value={v.id}>{v.name}{v.is_active_approved ? "" : " (Pending Approval)"}</option>
            ))}
          </select>
          <div className="flex justify-end gap-2 mt-4">
            <button className="vb-btn ghost sm" onClick={onClose}>Cancel</button>
            <button className="vb-btn sm" onClick={onConfirm} disabled={!picked}
              data-testid="pest-assign-confirm">Assign</button>
          </div>
        </>
      )}
    </Modal>
  );
}

function LogVisitModal({ site, onClose, onDone }) {  // unused — kept as a stub in case of rollback
  return null;
}

/* ------------------------- MONTHLY VISIT TRACKER ------------------------- */

/** 12-row monthly tracker per site. Current year always shown in full;
 * past years collapse to a one-line summary (click to expand). Future
 * months render greyed out — they can't be uploaded yet. */
function MonthlyVisitTracker({ site, canUpload, onChanged }) {
  const { notify, confirm } = useDialog();
  const [visits, setVisits] = useState([]);
  const [busy, setBusy] = useState(null);   // "YYYY-MM" currently uploading
  const [expandedYears, setExpandedYears] = useState(() => new Set());
  const [docCache, setDocCache] = useState({});   // doc_id -> doc record

  const load = async () => {
    try {
      const { data } = await api.get("/pest-control/visits", { params: { site_id: site.site_id, months: 120 } });
      setVisits(data.visits || []);
    } catch (e) {
      await notify({ title: "Couldn't load visit history", message: formatErr(e.response?.data?.detail), variant: "err" });
    }
  };
  useEffect(() => { load(); }, [site.site_id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch doc records (for filename / download link) for any visit that references one.
  useEffect(() => {
    const ids = visits.filter((v) => v.report_doc_id && !docCache[v.report_doc_id]).map((v) => v.report_doc_id);
    if (!ids.length) return;
    (async () => {
      try {
        const { data } = await api.get("/documents", {
          params: { owner_type: "pest_site", owner_id: site.site_id, requirement_kind: "pest_visit_report" },
        });
        const map = {};
        for (const d of data.documents || []) map[d.id] = d;
        setDocCache((prev) => ({ ...prev, ...map }));
      } catch { /* swallow */ }
    })();
  }, [visits, site.site_id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const now = new Date();
  const curYear = now.getFullYear();
  const curMonth = now.getMonth() + 1;
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const byYearMonth = {};
  for (const v of visits) {
    const [y, m] = (v.visit_month || "").split("-").map(Number);
    if (!y || !m) continue;
    byYearMonth[y] = byYearMonth[y] || {};
    byYearMonth[y][m] = v;
  }
  const yearsSeen = new Set([curYear, ...Object.keys(byYearMonth).map(Number)]);
  const years = Array.from(yearsSeen).sort((a, b) => b - a);

  const uploadForMonth = async (year, month, file) => {
    const ym = `${year}-${String(month).padStart(2, "0")}`;
    setBusy(ym);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("owner_type", "pest_site");
      fd.append("owner_id", site.site_id);
      fd.append("site_id", site.site_id);
      fd.append("requirement_kind", "pest_visit_report");
      fd.append("notes", `Monthly visit ${ym}`);
      const { data: doc } = await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/pest-control/visits", {
        site_id: site.site_id,
        vendor_id: site.vendor_id,
        visit_month: ym,
        visit_date: `${ym}-01`,
        report_doc_id: doc.id,
      });
      await load();
      onChanged?.();
    } catch (e) {
      await notify({ title: "Upload failed", message: formatErr(e.response?.data?.detail), variant: "err" });
    } finally { setBusy(null); }
  };

  const removeMonth = async (year, month) => {
    const v = byYearMonth[year]?.[month];
    if (!v) return;
    const ok = await confirm({ title: `Remove ${MONTHS[month - 1]} ${year} report?`, message: "The uploaded file will be soft-deleted; the month flips back to Missing." });
    if (!ok) return;
    try {
      if (v.report_doc_id) await api.delete(`/documents/${v.report_doc_id}`);
      // Re-upsert the visit with no doc_id (or simply remove via raw delete? Simpler: keep visit but clear doc)
      await api.post("/pest-control/visits", {
        site_id: site.site_id, vendor_id: site.vendor_id,
        visit_month: v.visit_month, visit_date: v.visit_date, report_doc_id: null,
      });
      await load();
      onChanged?.();
    } catch (e) {
      await notify({ title: "Couldn't remove", message: formatErr(e.response?.data?.detail), variant: "err" });
    }
  };

  const download = async (docId) => {
    try {
      const d = docCache[docId];
      const res = await api.get(`/documents/${docId}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: d?.content_type }));
      const a = document.createElement("a"); a.href = url; a.download = d?.filename || docId;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      await notify({ title: "Download failed", message: formatErr(e.response?.data?.detail), variant: "err" });
    }
  };

  const renderMonth = (year, month) => {
    const isFuture = year > curYear || (year === curYear && month > curMonth);
    const v = byYearMonth[year]?.[month];
    const ym = `${year}-${String(month).padStart(2, "0")}`;
    const hasDoc = !!v?.report_doc_id;
    return (
      <div key={ym} className="flex items-center gap-2 py-1 text-[12px]"
        style={{ borderBottom: "1px solid var(--vb-border)", opacity: isFuture ? 0.45 : 1 }}
        data-testid={`pest-month-${site.site_id}-${ym}`}>
        <span style={{ minWidth: 44, fontWeight: 600 }}>{MONTHS[month - 1]}</span>
        {isFuture ? (
          <span className="opacity-60">— not yet</span>
        ) : hasDoc ? (
          <>
            <CheckCircle2 size={12} style={{ color: "var(--vb-accent)" }} />
            <button onClick={() => download(v.report_doc_id)} className="underline"
              style={{ background: "none", border: "none", padding: 0, color: "var(--vb-text-2)", cursor: "pointer" }}>
              {docCache[v.report_doc_id]?.filename || "Download report"}
            </button>
            <span className="opacity-60 text-[11px]">· by {v.logged_by_name}</span>
            {canUpload && (
              <button onClick={() => removeMonth(year, month)} className="ml-auto"
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }} title="Remove">
                <Trash2 size={12} className="opacity-60 hover:opacity-100" />
              </button>
            )}
          </>
        ) : (
          <>
            <XCircle size={12} style={{ color: "var(--vb-error)" }} />
            <span style={{ color: "var(--vb-error)", fontWeight: 600 }}>Missing</span>
            {canUpload && (
              <label className="vb-btn sm ghost ml-auto" style={{ cursor: "pointer" }}>
                <Upload size={11} /> {busy === ym ? "…" : "Upload"}
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.docx" style={{ display: "none" }}
                  disabled={busy === ym}
                  onChange={(e) => { if (e.target.files?.[0]) uploadForMonth(year, month, e.target.files[0]); e.target.value = ""; }}
                  data-testid={`pest-month-upload-${site.site_id}-${ym}`} />
              </label>
            )}
          </>
        )}
      </div>
    );
  };

  const toggleYear = (year) => setExpandedYears((prev) => {
    const next = new Set(prev);
    if (next.has(year)) next.delete(year); else next.add(year);
    return next;
  });

  return (
    <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--vb-border)" }}
      data-testid={`pest-month-tracker-${site.site_id}`}>
      <h4 className="text-[12px] font-semibold opacity-80 mb-2">Monthly visit reports</h4>
      {years.map((year) => {
        const yearVisits = byYearMonth[year] || {};
        const count = Object.values(yearVisits).filter((x) => x.report_doc_id).length;
        if (year === curYear) {
          // Current year: render all 12 months inline (future ones greyed).
          return (
            <div key={year} className="mb-2">
              <div className="text-[12px] font-semibold mb-1 opacity-80">{year} · {count}/12 reports</div>
              {Array.from({ length: 12 }, (_, i) => renderMonth(year, i + 1))}
            </div>
          );
        }
        // Past year — collapsed summary row; click to expand.
        const allDone = count === 12;
        const expanded = expandedYears.has(year);
        return (
          <div key={year} className="mt-2">
            <button onClick={() => toggleYear(year)} data-testid={`pest-year-toggle-${site.site_id}-${year}`}
              className="flex items-center gap-2 w-full text-[12px] px-2 py-1.5 rounded"
              style={{
                background: allDone ? "rgba(34,197,94,0.08)" : "rgba(245,158,11,0.10)",
                border: `1px solid ${allDone ? "rgba(34,197,94,0.35)" : "rgba(245,158,11,0.45)"}`,
                cursor: "pointer", color: "var(--vb-text-2)", textAlign: "left",
              }}>
              {allDone
                ? <CheckCircle2 size={12} style={{ color: "var(--vb-accent)" }} />
                : <AlertTriangle size={12} style={{ color: "var(--vb-warning)" }} />}
              <strong>{year}</strong>
              <span>— {count}/12 reports {allDone ? "" : `· ${12 - count} missing`}</span>
              <ChevronRight size={12} style={{ marginLeft: "auto", transform: expanded ? "rotate(90deg)" : "none", transition: "transform 0.15s" }} />
            </button>
            {expanded && (
              <div className="mt-1">{Array.from({ length: 12 }, (_, i) => renderMonth(year, i + 1))}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------- APPROVED VENDOR SUB-VIEW ------------------------- */

function ApprovedVendorSection() {
  const { notify } = useDialog();
  const { user } = useAuth();
  const { isAllSites } = useSite();
  const [vendors, setVendors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);     // vendor object or {} (new)
  const canEdit = ["owner", "manager"].includes(user?.role);

  // Tenant-wide Vendor Dossier filing was retired from this page
  // (2026-06-23) — all Documentation filing now lives on the Reports
  // page so users have one consistent place.

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/vendors");
      setVendors(data.vendors || []);
    } catch (e) {
      await notify({ title: "Couldn't load vendors", message: formatErr(e.response?.data?.detail), variant: "err" });
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  if (busy && vendors.length === 0)
    return <div className="vb-card p-6 opacity-70" data-testid="vendors-loading">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h2 className="vb-heading text-2xl m-0 flex items-center gap-2" style={{ color: "var(--vb-text-2)" }}>
            <Building2 size={20} /> Approved Vendor
          </h2>
          <p className="text-sm opacity-70 m-0">
            Master list of suppliers. Upload the signed evaluation declaration form, the vendor license, and any MSDS sheets.
          </p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            <button className="vb-btn" onClick={() => setEditing({})} data-testid="vendor-add">
              <Plus size={13} /> Add vendor
            </button>
          </div>
        )}
      </div>

      {!isAllSites && (
        <div className="vb-card p-3 text-[12px]" data-testid="vendors-allsites-banner"
          style={{ background: "var(--vb-bg-2,#faf6f1)" }}>
          The Approved Vendor list is <strong>tenant-wide</strong> — the master site filter doesn&apos;t narrow this list.
          Each vendor has its own &quot;serves which sites&quot; assignment.
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        {vendors.map((v) => (
          <VendorRow key={v.id} vendor={v} canEdit={canEdit} onEdit={() => setEditing(v)} onChanged={load} />
        ))}
        {vendors.length === 0 && (
          <div className="vb-card p-6 opacity-70" data-testid="vendors-empty">
            No vendors yet. Click <strong>Add vendor</strong> to start.
          </div>
        )}
      </div>

      {editing && (
        <VendorModal initial={editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function VendorRow({ vendor, canEdit, onEdit, onChanged }) {
  const { notify, confirm } = useDialog();
  const [expanded, setExpanded] = useState(false);
  const [perfOpen, setPerfOpen] = useState(false);

  const decision = async (outcome) => {
    try {
      await api.post(`/vendors/${vendor.id}/decision`, { outcome });
      onChanged();
    } catch (e) {
      await notify({ title: "Couldn't update", message: formatErr(e.response?.data?.detail), variant: "err" });
    }
  };
  const remove = async () => {
    const ok = await confirm({ title: `Delete ${vendor.name}?`, message: "This vendor and its uploaded documents will be removed." });
    if (!ok) return;
    try {
      await api.delete(`/vendors/${vendor.id}`);
      onChanged();
    } catch (e) {
      await notify({ title: "Couldn't delete", message: formatErr(e.response?.data?.detail), variant: "err" });
    }
  };

  const statusPill = vendor.performance_delisted
    ? <span className="vb-pill err" data-testid={`vendor-status-${vendor.id}`}><XCircle size={11} /> Delisted</span>
    : vendor.evaluation_outcome === "reject"
      ? <span className="vb-pill err" data-testid={`vendor-status-${vendor.id}`}><XCircle size={11} /> Rejected</span>
      : vendor.is_active_approved
        ? <span className="vb-pill ok" data-testid={`vendor-status-${vendor.id}`}><CheckCircle2 size={11} /> Approved</span>
        : vendor.evaluation_outcome === "approve"
          ? <span className="vb-pill warn" data-testid={`vendor-status-${vendor.id}`}><AlertTriangle size={11} /> Approved · re-eval needed</span>
          : <span className="vb-pill" data-testid={`vendor-status-${vendor.id}`}><Clock size={11} /> Pending</span>;

  const reevalPill = vendor.reeval_status === "overdue"
    ? <span className="vb-pill err"><AlertTriangle size={11} /> Re-eval overdue ({Math.abs(vendor.days_to_reeval)}d)</span>
    : vendor.reeval_status === "due_soon"
      ? <span className="vb-pill warn"><Clock size={11} /> Re-eval in {vendor.days_to_reeval}d</span>
      : null;

  const perfPill = vendor.performance_status === "unrated"
    ? <span className="vb-pill" data-testid={`vendor-perf-${vendor.id}`}
        style={{ background: "var(--vb-bg-2,#faf6f1)" }}>
        <Star size={11} /> Not evaluated
      </span>
    : vendor.performance_delisted
      ? <span className="vb-pill err" data-testid={`vendor-perf-${vendor.id}`}>
          <Star size={11} /> Score {vendor.performance_total}/{vendor.performance_max} · Delisted
        </span>
      : <span className="vb-pill ok" data-testid={`vendor-perf-${vendor.id}`}>
          <Star size={11} /> Score {vendor.performance_total}/{vendor.performance_max}
        </span>;

  return (
    <div className="vb-card p-4" data-testid={`vendor-${vendor.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="vb-heading text-base m-0" style={{ color: "var(--vb-text-2)" }}>{vendor.name}</h3>
            <span className="vb-pill" style={{ background: "var(--vb-bg-2,#faf6f1)" }}>{vendor.category}</span>
            {statusPill}
            {reevalPill}
            {perfPill}
          </div>
          <div className="text-[12px] opacity-70 mt-1">
            {vendor.product_service || "—"}
            {vendor.contact_person && <> · Contact: {vendor.contact_person}</>}
            {vendor.contact_number && <> · {vendor.contact_number}</>}
          </div>
          <div className="text-[11px] opacity-60 mt-1">
            Serves: {vendor.site_assignments === "all" ? "All sites" : `${(vendor.site_assignments || []).length} site(s)`}
            {vendor.last_evaluation_date && <> · Last evaluated: {vendor.last_evaluation_date}</>}
            {vendor.next_evaluation_due && <> · Next due: {vendor.next_evaluation_due}</>}
            {vendor.performance_evaluated_date && <> · Performance: {vendor.performance_evaluated_date}</>}
          </div>
        </div>
        {canEdit && (
          <div className="flex gap-1 flex-wrap">
            <button className="vb-btn sm ghost" onClick={() => setPerfOpen(true)}
              data-testid={`vendor-perf-open-${vendor.id}`}
              title="Score the vendor against the 5 performance criteria">
              <Star size={12} /> Evaluate performance
            </button>
            {vendor.evaluation_outcome !== "approve" && (
              <button className="vb-btn sm" onClick={() => decision("approve")}
                data-testid={`vendor-approve-${vendor.id}`}>
                <CheckCircle2 size={12} /> Approve
              </button>
            )}
            {vendor.evaluation_outcome !== "reject" && (
              <button className="vb-btn sm ghost" onClick={() => decision("reject")}
                data-testid={`vendor-reject-${vendor.id}`}>
                <XCircle size={12} /> Reject
              </button>
            )}
            <button className="vb-btn sm ghost" onClick={onEdit} data-testid={`vendor-edit-${vendor.id}`}>
              Edit
            </button>
            <button className="vb-btn sm ghost" onClick={remove} data-testid={`vendor-delete-${vendor.id}`}>
              <Trash2 size={12} />
            </button>
          </div>
        )}
      </div>

      {(() => {
        // Categorise: pest control gets its own bundle; food categories
        // need an eval form + license; non-food need only the eval form.
        // Supplementary is ALWAYS optional and never counted.
        const FOOD = ["Produce", "Meat / Seafood", "Dairy", "Dry Goods"];
        const isPest = vendor.category === "Pest Control";
        const isFood = FOOD.includes(vendor.category);
        const required = isPest
          ? [
              { label: "Evaluation declaration form", ok: vendor.has_evaluation_form },
              { label: "Pest Control Contract",       ok: vendor.has_pest_contract },
              { label: "Company License & Tech License", ok: vendor.has_license },
              { label: "SDS",                          ok: vendor.has_msds },
            ]
          : isFood
            ? [
                { label: "Evaluation declaration form", ok: vendor.has_evaluation_form },
                { label: "Vendor license / accreditation", ok: vendor.has_license },
              ]
            : [
                { label: "Evaluation declaration form", ok: vendor.has_evaluation_form },
              ];
        const done = required.filter((r) => r.ok).length;
        const total = required.length;
        const allDone = done === total;
        return (
          <button onClick={() => setExpanded(!expanded)}
            data-testid={`vendor-toggle-${vendor.id}`}
            className="flex items-center gap-2 mt-3 px-3 py-2 rounded text-[13px] w-full"
            style={{
              background: allDone ? "rgba(34,197,94,0.08)" : "rgba(245,158,11,0.10)",
              border: `1px solid ${allDone ? "rgba(34,197,94,0.35)" : "rgba(245,158,11,0.45)"}`,
              cursor: "pointer", color: "var(--vb-text-2)",
              fontWeight: 600, textAlign: "left",
            }}>
            {allDone
              ? <CheckCircle2 size={14} style={{ color: "var(--vb-accent)" }} />
              : <AlertTriangle size={14} style={{ color: "var(--vb-warning)" }} />}
            <span>{expanded ? "Hide" : "Show"} documents</span>
            <span className="vb-pill" data-testid={`vendor-docs-counter-${vendor.id}`}
              style={{
                background: allDone ? "rgba(34,197,94,0.18)" : "rgba(245,158,11,0.22)",
                color: allDone ? "var(--vb-accent)" : "var(--vb-warning)",
                fontWeight: 700,
              }}>
              {done}/{total} uploaded
            </span>
            {!allDone && (
              <span className="text-[11px] font-normal opacity-80 ml-1">
                · {total - done} missing — required for approval
              </span>
            )}
            <ChevronRight size={14}
              style={{ marginLeft: "auto", transform: expanded ? "rotate(90deg)" : "none", transition: "transform 0.15s" }} />
          </button>
        );
      })()}

      {expanded && (() => {
        const FOOD = ["Produce", "Meat / Seafood", "Dairy", "Dry Goods"];
        const isPest = vendor.category === "Pest Control";
        const isFood = FOOD.includes(vendor.category);
        return (
          <div className="mt-3 pt-3 space-y-2" style={{ borderTop: "1px solid var(--vb-border)" }}>
            {/* Evaluation form — required for ALL categories */}
            <DocumentSlot label="Evaluation declaration form (F01a)"
              ownerType="vendor" ownerId={vendor.id} requirementKind="vendor_evaluation_form"
              canUpload={canEdit} onChanged={onChanged} expiryHint="Signed by the supplier" />
            {/* Pest Control extras */}
            {isPest && (
              <DocumentSlot label="Pest Control Contract"
                ownerType="vendor" ownerId={vendor.id} requirementKind="vendor_pest_contract"
                canUpload={canEdit} onChanged={onChanged} expiryHint="Service agreement; track end date" />
            )}
            {/* License — required for Pest Control + Food categories */}
            {(isPest || isFood) && (
              <DocumentSlot label={isPest ? "Company License & Technician License" : "Vendor license / accreditation"}
                ownerType="vendor" ownerId={vendor.id} requirementKind="vendor_license"
                canUpload={canEdit} onChanged={onChanged}
                expiryHint={isPest ? "Upload both licenses; track validity" : "Track licence validity"} />
            )}
            {/* SDS — Pest Control only */}
            {isPest && (
              <DocumentSlot label="SDS of Pest Control Chemicals"
                ownerType="vendor" ownerId={vendor.id} requirementKind="vendor_msds"
                canUpload={canEdit} onChanged={onChanged} expiryHint="Update when chemicals change" />
            )}
            {/* Supplementary — always available, optional, multiple files allowed,
                NOT counted toward the required-doc tally. */}
            <DocumentSlot label="Supplementary documents (optional)"
              ownerType="vendor" ownerId={vendor.id} requirementKind="vendor_supplementary"
              canUpload={canEdit} onChanged={onChanged}
              expiryHint="Anything else you'd like to keep on file" />
          </div>
        );
      })()}

      {perfOpen && (
        <VendorPerformanceModal vendor={vendor} onClose={() => setPerfOpen(false)}
          onSaved={() => { setPerfOpen(false); onChanged(); }} />
      )}
    </div>
  );
}

const PERF_CRITERIA = [
  { key: "customer_service",   label: "Quality of Customer Service" },
  { key: "delivery_adherence", label: "Adherence to Delivery Instruction / Requests" },
  { key: "lead_time",          label: "Lead time required to perform task" },
  { key: "quality_safety",     label: "Quality & Food Safety / Competency of personnel" },
  { key: "pricing",            label: "Competitive Pricing" },
];

function VendorPerformanceModal({ vendor, onClose, onSaved }) {
  const { notify, confirm } = useDialog();
  const existing = vendor.performance_scores || {};
  const [scores, setScores] = useState(() =>
    PERF_CRITERIA.reduce((acc, c) => ({ ...acc, [c.key]: Number(existing[c.key]) || 3 }), {})
  );
  const [evalDate, setEvalDate] = useState(
    vendor.performance_evaluated_date || new Date().toISOString().slice(0, 10),
  );
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const total = PERF_CRITERIA.reduce((s, c) => s + (Number(scores[c.key]) || 0), 0);
  const delisted = total < 15;

  const submit = async () => {
    if (delisted) {
      const ok = await confirm({
        title: "Score below threshold (15)",
        message: `Total ${total}/25 — saving will mark ${vendor.name} as DELISTED. Continue?`,
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      await api.post(`/vendors/${vendor.id}/performance`, {
        ...scores,
        evaluation_date: evalDate || null,
        notes,
      });
      onSaved();
    } catch (e) {
      await notify({ title: "Couldn't save", message: formatErr(e.response?.data?.detail), variant: "err" });
    } finally { setBusy(false); }
  };

  return (
    <Modal title={`Evaluation of Performance · ${vendor.name}`} onClose={onClose}
      testid="vendor-perf-modal" wide>
      <p className="text-[12px] opacity-70 m-0 mb-3">
        Score each criterion from <strong>1 (poor)</strong> to <strong>5 (excellent)</strong>.
        Total is out of 25. A vendor scoring <strong>below 15</strong> is automatically delisted.
      </p>

      <div className="vb-card p-3 mb-3 text-[12px]" data-testid="perf-legend"
        style={{ background: "var(--vb-bg-2,#faf6f1)" }}>
        <div className="font-semibold mb-1" style={{ color: "var(--vb-text-2)" }}>Scoring legend</div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-1">
          {[
            { n: 5, label: "Excellent" },
            { n: 4, label: "Very Good" },
            { n: 3, label: "Satisfy" },
            { n: 2, label: "Poor" },
            { n: 1, label: "Bad" },
          ].map((s) => (
            <div key={s.n} className="flex items-center gap-2">
              <span style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 22, height: 22, borderRadius: 11, fontWeight: 700,
                background: "var(--vb-accent)", color: "white", fontSize: 11,
              }}>{s.n}</span>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-3">
        {PERF_CRITERIA.map((c) => (
          <div key={c.key} className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[13px] flex-1 min-w-[200px]">{c.label}</div>
            <div className="flex items-center gap-1" data-testid={`perf-row-${c.key}`}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button"
                  onClick={() => setScores({ ...scores, [c.key]: n })}
                  data-testid={`perf-${c.key}-${n}`}
                  className="vb-btn sm"
                  style={{
                    minWidth: 32, padding: "4px 8px",
                    background: scores[c.key] === n ? "var(--vb-accent)" : "transparent",
                    color: scores[c.key] === n ? "white" : "var(--vb-text-2)",
                    border: "1px solid var(--vb-border)",
                  }}>
                  {n}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 mt-4 p-3 rounded"
        style={{
          background: delisted ? "rgba(239,68,68,0.10)" : "rgba(34,197,94,0.10)",
          border: `1px solid ${delisted ? "rgba(239,68,68,0.4)" : "rgba(34,197,94,0.4)"}`,
        }}>
        <div className="text-[13px]">
          <strong>Total:&nbsp;</strong>
          <span data-testid="perf-total">{total} / 25</span>
        </div>
        {delisted
          ? <span className="vb-pill err"><XCircle size={11} /> Delisted (&lt; 15)</span>
          : <span className="vb-pill ok"><CheckCircle2 size={11} /> In good standing</span>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 text-[13px]">
        <Field label="Evaluation date">
          <input type="date" className="vb-input" value={evalDate}
            onChange={(e) => setEvalDate(e.target.value)} data-testid="perf-date" />
        </Field>
        <Field label="Notes (optional)">
          <input className="vb-input" value={notes}
            onChange={(e) => setNotes(e.target.value)} data-testid="perf-notes"
            placeholder="Evidence, follow-up actions…" />
        </Field>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button className="vb-btn ghost sm" onClick={onClose}>Cancel</button>
        <button className="vb-btn sm" onClick={submit} disabled={busy}
          data-testid="perf-save">{busy ? "Saving…" : "Save evaluation"}</button>
      </div>
    </Modal>
  );
}

function VendorModal({ initial, onClose, onSaved }) {
  const { notify } = useDialog();
  const { sites } = useSite();
  const isNew = !initial?.id;
  const [f, setF] = useState({
    name: initial?.name || "",
    category: initial?.category || "Other",
    contact_person: initial?.contact_person || "",
    contact_number: initial?.contact_number || "",
    contact_email: initial?.contact_email || "",
    product_service: initial?.product_service || "",
    site_assignments: initial?.site_assignments || "all",
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const isAll = f.site_assignments === "all";
  const selectedSites = Array.isArray(f.site_assignments) ? f.site_assignments : [];

  const submit = async () => {
    if (!f.name.trim()) {
      await notify({ title: "Name is required", variant: "warn" });
      return;
    }
    setBusy(true);
    try {
      // `last_evaluation_date`, `evaluated_by` and `verified_by` are
      // intentionally omitted here — they're populated downstream by the
      // Approve/Reject decision (auto-stamps `last_evaluation_date`) and
      // by the Evaluate Performance modal (writes `performance_evaluated_by`
      // and `performance_evaluated_date`). Capturing them on Add Vendor
      // was redundant data-entry. Existing records keep their values.
      if (isNew) await api.post("/vendors", f);
      else await api.patch(`/vendors/${initial.id}`, f);
      onSaved();
    } catch (e) {
      await notify({ title: "Couldn't save", message: formatErr(e.response?.data?.detail), variant: "err" });
    } finally { setBusy(false); }
  };

  return (
    <Modal title={isNew ? "Add vendor" : `Edit ${initial.name}`} onClose={onClose} testid="vendor-modal" wide>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[13px]">
        <Field label="Vendor name *">
          <input className="vb-input" value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="vmod-name" />
        </Field>
        <Field label="Category">
          <select className="vb-input" value={f.category}
            onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="vmod-category">
            {["Pest Control", "Produce", "Meat / Seafood", "Dairy", "Dry Goods", "Packaging", "Chemicals / Sanitisers", "Calibration", "Transport / Logistics", "Other"].map((c) =>
              <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Product / Service">
          <input className="vb-input" value={f.product_service}
            onChange={(e) => setF({ ...f, product_service: e.target.value })} data-testid="vmod-product" />
        </Field>
        <Field label="Contact person">
          <input className="vb-input" value={f.contact_person}
            onChange={(e) => setF({ ...f, contact_person: e.target.value })} />
        </Field>
        <Field label="Contact number">
          <input className="vb-input" value={f.contact_number}
            onChange={(e) => setF({ ...f, contact_number: e.target.value })} />
        </Field>
        <Field label="Contact email">
          <input className="vb-input" value={f.contact_email}
            onChange={(e) => setF({ ...f, contact_email: e.target.value })} />
        </Field>
      </div>

      <div className="mt-3 p-2 rounded text-[11px]" style={{ background: "var(--vb-bg-2,#faf6f1)", border: "1px solid var(--vb-border)", color: "var(--vb-text-2)" }}>
        <b>Note —</b> Evaluation date and evaluator name are captured automatically:
        the <b>Approve / Reject</b> button stamps the last evaluation date,
        and the <b>Evaluate performance</b> modal records who scored the vendor.
      </div>

      <div className="mt-3">
        <label className="block text-[12px] font-medium mb-1">Serves which sites?</label>
        <div className="flex items-center gap-3 text-[13px] mb-2">
          <label className="flex items-center gap-1">
            <input type="radio" checked={isAll} onChange={() => setF({ ...f, site_assignments: "all" })}
              data-testid="vmod-sites-all" /> All sites
          </label>
          <label className="flex items-center gap-1">
            <input type="radio" checked={!isAll} onChange={() => setF({ ...f, site_assignments: [] })}
              data-testid="vmod-sites-specific" /> Specific sites
          </label>
        </div>
        {!isAll && (
          <div className="grid grid-cols-2 gap-1 text-[12px]">
            {sites.map((s) => (
              <label key={s.id} className="flex items-center gap-1">
                <input type="checkbox" checked={selectedSites.includes(s.id)}
                  onChange={(e) => {
                    const nextList = e.target.checked
                      ? [...selectedSites, s.id]
                      : selectedSites.filter((x) => x !== s.id);
                    setF({ ...f, site_assignments: nextList });
                  }} />
                {s.name}
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="mt-3">
        <label className="block text-[12px] font-medium mb-1">Notes</label>
        <textarea className="vb-input" rows={2} value={f.notes}
          onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button className="vb-btn ghost sm" onClick={onClose}>Cancel</button>
        <button className="vb-btn sm" onClick={submit} disabled={busy}
          data-testid="vmod-save">{busy ? "Saving…" : "Save"}</button>
      </div>
    </Modal>
  );
}

/* ------------------------- SHARED ------------------------- */

function DocumentSlot({ label, ownerType, ownerId, siteId, requirementKind, canUpload, onChanged, expiryHint }) {
  const { notify, confirm } = useDialog();
  const [docs, setDocs] = useState([]);
  const [hasExpiry, setHasExpiry] = useState(false);
  const [validUntil, setValidUntil] = useState("");
  const [file, setFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);   // bump to remount <input type="file"> and clear its DOM value
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/documents", {
        params: { owner_type: ownerType, owner_id: ownerId, requirement_kind: requirementKind },
      });
      setDocs(data.documents || []);
    } catch { /* swallow */ }
  };
  useEffect(() => { load(); }, [ownerId, requirementKind]);  // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("owner_type", ownerType);
      fd.append("owner_id", ownerId);
      if (siteId) fd.append("site_id", siteId);
      fd.append("requirement_kind", requirementKind);
      if (hasExpiry && validUntil) fd.append("valid_until", validUntil);
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFile(null); setValidUntil(""); setHasExpiry(false);
      setFileInputKey((k) => k + 1);   // remount the file input so its DOM value resets to empty
      await load();
      onChanged?.();
    } catch (e) {
      await notify({ title: "Upload failed", message: formatErr(e.response?.data?.detail), variant: "err" });
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    const ok = await confirm({ title: "Delete document?", message: "This action soft-deletes the file; previous versions remain accessible to platform admins." });
    if (!ok) return;
    try {
      await api.delete(`/documents/${id}`);
      await load();
      onChanged?.();
    } catch (e) {
      await notify({ title: "Couldn't delete", message: formatErr(e.response?.data?.detail), variant: "err" });
    }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: d.content_type }));
      const a = document.createElement("a"); a.href = url; a.download = d.filename || d.id;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      await notify({ title: "Download failed", message: formatErr(e.response?.data?.detail), variant: "err" });
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const hasActive = docs.some((d) => !d.valid_until || d.valid_until >= today);

  return (
    <div className="text-[12px]" data-testid={`docslot-${requirementKind}-${ownerId}`}>
      <div className="flex items-center gap-2 mb-1">
        {hasActive
          ? <CheckCircle2 size={12} style={{ color: "var(--vb-accent)" }} />
          : <XCircle size={12} style={{ color: docs.length ? "var(--vb-warning)" : "var(--vb-error)" }} />}
        <strong>{label}</strong>
        {expiryHint && <span className="opacity-60 text-[11px]">· {expiryHint}</span>}
      </div>
      {docs.length > 0 && (
        <ul className="m-0 p-0 mb-2" style={{ listStyle: "none" }}>
          {docs.map((d) => {
            const expired = d.valid_until && d.valid_until < today;
            return (
              <li key={d.id} className="flex items-center gap-2 py-0.5"
                data-testid={`doc-${d.id}`}>
                <button onClick={() => download(d)} className="underline text-left"
                  style={{ background: "none", border: "none", padding: 0, color: "var(--vb-text-2)", cursor: "pointer" }}>
                  {d.filename}
                </button>
                {d.valid_until && (
                  <span className={`text-[11px] ${expired ? "" : "opacity-60"}`}
                    style={expired ? { color: "var(--vb-error)" } : {}}>
                    · {expired ? "Expired" : "Valid until"} {d.valid_until}
                  </span>
                )}
                <span className="text-[11px] opacity-50">· by {d.uploaded_by_name}</span>
                {canUpload && (
                  <button onClick={() => remove(d.id)} title="Delete"
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 0, marginLeft: "auto" }}>
                    <Trash2 size={12} className="opacity-60 hover:opacity-100" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {canUpload && (
        <div className="flex items-center gap-2 flex-wrap">
          <input key={fileInputKey} type="file" className="vb-input flex-1" style={{ minWidth: 180 }}
            accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid={`docslot-file-${requirementKind}`} />
          <label className="flex items-center gap-1 text-[11px] whitespace-nowrap"
            data-testid={`docslot-hasexpiry-${requirementKind}`}>
            <input type="checkbox" checked={hasExpiry}
              onChange={(e) => { setHasExpiry(e.target.checked); if (!e.target.checked) setValidUntil(""); }} />
            Has expiry date
          </label>
          {hasExpiry && (
            <input type="date" className="vb-input" style={{ width: 150 }}
              value={validUntil} onChange={(e) => setValidUntil(e.target.value)}
              title="Document validity end date"
              data-testid={`docslot-validuntil-${requirementKind}`} />
          )}
          <button className="vb-btn sm" onClick={upload} disabled={busy || !file || (hasExpiry && !validUntil)}
            data-testid={`docslot-upload-${requirementKind}`}>
            <Upload size={12} /> {busy ? "…" : "Upload"}
          </button>
        </div>
      )}
    </div>
  );
}

function Modal({ title, onClose, children, wide, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.4)" }}
      onClick={onClose} data-testid={testid}>
      <div className="vb-card p-5" style={{ maxWidth: wide ? 720 : 480, width: "100%", maxHeight: "90vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}>
        <h2 className="vb-heading text-xl m-0 mb-3" style={{ color: "var(--vb-text-2)" }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[12px] font-medium mb-1">{label}</label>
      {children}
    </div>
  );
}
